/*
 * ---------------------------------------------------------------------------
 * The Little Library — Products page
 * File: assets/tll-products.js
 *
 * Custom elements used by the tll-products-* sections:
 *   <tllp-shop>         the filter and sort bar over the grouped product grid
 *   <tllp-quantity>     the − / + stepper inside each add-to-basket form
 *   <tllp-look-inside>  the "Look inside" trigger and its page viewer dialog
 *
 * Adding to the basket itself is Dawn's product-form.js: the stepper's input
 * is named "quantity" inside the form, which product-form.js already reads.
 *
 * Custom elements upgrade whenever their markup is inserted, so both keep
 * working after the theme editor re-renders a section.
 * ---------------------------------------------------------------------------
 */

(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- option picker price --------------------------------------------- */
  // Each card with options renders one <template data-tllp-price> per
  // variant; choosing an option swaps the card's price for that one. One
  // delegated listener covers every card, including editor re-renders.
  if (!window.tllpPriceListener) {
    window.tllpPriceListener = true;
    document.addEventListener('change', (event) => {
      const select = event.target.closest?.('.tllp-select__input');
      if (!select) return;
      const card = select.closest('.tllp-card');
      const price = card?.querySelector('.tllp-card__price');
      const template = card?.querySelector(`template[data-tllp-price="${select.value}"]`);
      const next = template?.content.querySelector('.tllp-price');
      if (price && next) price.innerHTML = next.innerHTML;
    });
  }

  /* ---- shop: filter and sort ------------------------------------------- */
  // Filtering hides cards and whole groups in place. A price sort moves the
  // visible cards into one flat grid in price order — a real move, not CSS
  // `order`, so keyboard and screen reader order match what is on screen —
  // and Featured puts every card back where Liquid rendered it.
  if (!customElements.get('tllp-shop')) {
    customElements.define(
      'tllp-shop',
      class TllpShop extends HTMLElement {
        static SORTS = ['featured', 'price-asc', 'price-desc'];

        connectedCallback() {
          if (this.ready) return;
          this.ready = true;

          this.toolbar = this.querySelector('[data-tllp-toolbar]');
          this.results = this.querySelector('[data-tllp-results]');
          this.sortedGrid = this.querySelector('[data-tllp-sorted]');
          if (!this.toolbar || !this.results || !this.sortedGrid) return;

          this.chips = Array.from(this.toolbar.querySelectorAll('[data-tllp-filter]'));
          this.sortSelect = this.toolbar.querySelector('[data-tllp-sort]');
          this.countEl = this.toolbar.querySelector('[data-tllp-count]');
          this.groups = Array.from(this.results.querySelectorAll('[data-tllp-group]'));
          this.comparisons = Array.from(this.results.querySelectorAll('[data-tllp-compare]'));
          this.items = Array.from(this.results.querySelectorAll('[data-tllp-item]'));
          this.items.forEach((item, index) => {
            item.tllpHome = item.parentElement;
            item.tllpIndex = index;
          });

          const params = new URLSearchParams(window.location.search);
          const filters = this.chips.map((chip) => chip.dataset.tllpFilter);
          this.filter = filters.includes(params.get('show')) ? params.get('show') : 'all';
          this.sort = TllpShop.SORTS.includes(params.get('sort')) ? params.get('sort') : 'featured';
          if (this.sortSelect) this.sortSelect.value = this.sort;

          this.chips.forEach((chip) =>
            chip.addEventListener('click', () => this.update({ filter: chip.dataset.tllpFilter }))
          );
          this.sortSelect?.addEventListener('change', () => this.update({ sort: this.sortSelect.value }));

          this.toolbar.hidden = false;
          this.apply();

          // Which groups sit alone on their row depends on the column count,
          // so check again whenever the grid's width changes.
          if ('ResizeObserver' in window) {
            this.lastWidth = 0;
            this.resizeObserver = new ResizeObserver(([entry]) => {
              const width = Math.round(entry.contentRect.width);
              if (width === this.lastWidth) return;
              this.lastWidth = width;
              this.markAlone();
            });
            this.resizeObserver.observe(this.results);
          }

          if (this.dataset.sticky === 'true') {
            this.onScroll = () => {
              if (this.frame) return;
              this.frame = requestAnimationFrame(() => {
                this.frame = null;
                this.markStuck();
              });
            };
            window.addEventListener('scroll', this.onScroll, { passive: true });
            this.markStuck();
          }
        }

        disconnectedCallback() {
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
          this.resizeObserver?.disconnect();
        }

        update(change) {
          Object.assign(this, change);
          this.apply({ announce: true });
          this.syncUrl();
          this.keepInView();
        }

        isShown(item) {
          // "All" lists each product once; a group filter includes products
          // that are only "also shown under" it.
          if (this.filter === 'all') return !item.hasAttribute('data-tllp-secondary');
          return item.dataset.tllpGroupOf === this.filter;
        }

        apply({ announce = false } = {}) {
          this.chips.forEach((chip) =>
            chip.setAttribute('aria-pressed', String(chip.dataset.tllpFilter === this.filter))
          );

          const shown = this.items.filter((item) => this.isShown(item));
          this.items.forEach((item) => (item.hidden = !shown.includes(item)));

          if (this.sort === 'featured') {
            // Back home, in the order Liquid rendered them.
            if (this.sorted) {
              this.items.forEach((item) => item.tllpHome.append(item));
              this.sorted = false;
            }
            this.sortedGrid.hidden = true;
            this.groups.forEach((group) => {
              const inGroup = this.filter === 'all' || group.dataset.tllpGroup === this.filter;
              const count = group.querySelectorAll('[data-tllp-item]:not([hidden])').length;
              // The group spans as many grid columns as it shows cards.
              group.dataset.count = String(count);
              group.hidden = !inGroup || count === 0;
            });
            this.comparisons.forEach((table) => {
              table.hidden = !(this.filter === 'all' || table.dataset.tllpCompare === this.filter);
            });
            this.markAlone();
          } else {
            const direction = this.sort === 'price-asc' ? 1 : -1;
            shown
              .slice()
              .sort(
                (a, b) =>
                  direction * (Number(a.dataset.tllpPrice) - Number(b.dataset.tllpPrice)) ||
                  a.tllpIndex - b.tllpIndex
              )
              .forEach((item) => this.sortedGrid.append(item));
            this.sorted = true;
            this.groups.forEach((group) => (group.hidden = true));
            this.comparisons.forEach((table) => (table.hidden = true));
            this.sortedGrid.hidden = false;
          }

          if (this.countEl) {
            const text = `${shown.length} ${shown.length === 1 ? 'product' : 'products'}`;
            // Only announce what the shopper asked for, not the first render.
            this.countEl.setAttribute('aria-live', announce ? 'polite' : 'off');
            this.countEl.textContent = text;
          }
        }

        // A one-product group left alone on its row would leave most of the row
        // empty. Marked, it spans the row and its card lies sideways (see
        // section-tll-products.css). Rows are read from where groups landed.
        markAlone() {
          const visible = this.groups.filter((group) => !group.hidden);
          visible.forEach((group) => group.removeAttribute('data-alone'));
          if (this.sort !== 'featured') return;

          const rows = new Map();
          visible.forEach((group) => {
            const top = Math.round(group.getBoundingClientRect().top);
            rows.set(top, (rows.get(top) || []).concat(group));
          });
          rows.forEach((row) => {
            if (row.length === 1 && row[0].dataset.count === '1') row[0].setAttribute('data-alone', '');
          });
        }

        syncUrl() {
          const url = new URL(window.location.href);
          // Cleared and re-added in a fixed order, so a link reads the same
          // however the shopper got there.
          url.searchParams.delete('show');
          url.searchParams.delete('sort');
          if (this.filter !== 'all') url.searchParams.set('show', this.filter);
          if (this.sort !== 'featured') url.searchParams.set('sort', this.sort);
          window.history.replaceState(window.history.state, '', url);
        }

        // Filtering from a bar stuck far down the page would leave the shopper
        // looking at the gap where hidden cards were; bring the results up.
        keepInView() {
          const barBottom = this.toolbar.getBoundingClientRect().bottom;
          const resultsTop = this.results.getBoundingClientRect().top;
          if (resultsTop >= barBottom) return;
          window.scrollTo({
            top: window.scrollY + resultsTop - barBottom - 16,
            behavior: reduceMotion() ? 'auto' : 'smooth',
          });
        }

        markStuck() {
          const top = parseFloat(getComputedStyle(this.toolbar).top) || 0;
          const stuck =
            getComputedStyle(this.toolbar).position === 'sticky' &&
            this.toolbar.getBoundingClientRect().top <= top + 1 &&
            this.getBoundingClientRect().top < 0;
          this.toolbar.classList.toggle('is-stuck', stuck);
        }
      }
    );
  }

  /* ---- quantity stepper ------------------------------------------------ */
  if (!customElements.get('tllp-quantity')) {
    customElements.define(
      'tllp-quantity',
      class TllpQuantity extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;

          this.input = this.querySelector('input');
          this.minus = this.querySelector('[data-step="-1"]');
          this.plus = this.querySelector('[data-step="1"]');
          if (!this.input || !this.minus || !this.plus) return;

          // aria-disabled rather than disabled: a disabled button drops
          // keyboard focus to <body> the moment it reaches the limit.
          this.addEventListener('click', (event) => {
            const button = event.target.closest('[data-step]');
            if (!button || button.getAttribute('aria-disabled') === 'true') return;
            event.preventDefault();
            this.set(this.value + Number(button.dataset.step));
          });

          this.input.addEventListener('change', () => this.set(this.value));
          this.input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') this.set(this.value);
          });

          this.sync();
        }

        get min() {
          return parseInt(this.input.min, 10) || 1;
        }

        get max() {
          return parseInt(this.input.max, 10) || 99;
        }

        get value() {
          const value = parseInt(this.input.value, 10);
          return Number.isNaN(value) ? this.min : value;
        }

        set(value) {
          this.input.value = Math.min(this.max, Math.max(this.min, value));
          this.sync();
        }

        sync() {
          const value = this.value;
          this.minus.setAttribute('aria-disabled', String(value <= this.min));
          this.plus.setAttribute('aria-disabled', String(value >= this.max));
        }
      }
    );
  }

  /* ---- Look inside viewer ---------------------------------------------- */
  if (!customElements.get('tllp-look-inside')) {
    customElements.define(
      'tllp-look-inside',
      class TllpLookInside extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;

          this.trigger = this.querySelector('[data-tllp-open]');
          this.dialog = this.querySelector('dialog');
          if (!this.trigger || !this.dialog) return;

          this.track = this.dialog.querySelector('[data-tllp-track]');
          this.pages = Array.from(this.dialog.querySelectorAll('[data-tllp-page]'));
          this.prev = this.dialog.querySelector('[data-tllp-prev]');
          this.next = this.dialog.querySelector('[data-tllp-next]');
          this.current = this.dialog.querySelector('[data-tllp-current]');
          this.index = 0;

          this.trigger.addEventListener('click', () => this.open());
          this.dialog.querySelectorAll('[data-tllp-close]').forEach((button) =>
            button.addEventListener('click', () => this.dialog.close())
          );
          this.prev?.addEventListener('click', () => this.go(this.index - 1));
          this.next?.addEventListener('click', () => this.go(this.index + 1));

          // The panel fills the dialog box, so a click that lands on the
          // dialog element itself can only be on the backdrop.
          this.dialog.addEventListener('click', (event) => {
            if (event.target === this.dialog) this.dialog.close();
          });

          this.dialog.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              this.go(this.index + 1);
            } else if (event.key === 'ArrowLeft') {
              event.preventDefault();
              this.go(this.index - 1);
            }
          });

          this.dialog.addEventListener('close', () => {
            document.documentElement.classList.remove('tllp-locked');
            this.trigger.focus({ preventScroll: true });
          });

          // The counter follows whichever page is mostly in view, so swipes
          // and trackpad scrolls update it as well as the buttons.
          if (this.track && 'IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) this.update(this.pages.indexOf(entry.target));
                });
              },
              { root: this.track, threshold: 0.6 }
            );
            this.pages.forEach((page) => this.observer.observe(page));
          }

          this.update(0);
        }

        disconnectedCallback() {
          this.observer?.disconnect();
          if (this.dialog?.open) this.dialog.close();
          document.documentElement.classList.remove('tllp-locked');
        }

        open() {
          if (typeof this.dialog.showModal !== 'function') return;
          document.documentElement.classList.add('tllp-locked');
          this.dialog.showModal();
          this.go(0, true);
        }

        go(index, instant = false) {
          if (!this.track || this.pages.length === 0) return;
          const target = Math.min(this.pages.length - 1, Math.max(0, index));
          // The track is position: relative, so offsetLeft is measured from
          // its own start and unaffected by how far it is already scrolled.
          this.track.scrollTo({
            left: this.pages[target].offsetLeft,
            behavior: instant || reduceMotion() ? 'auto' : 'smooth',
          });
          this.update(target);
        }

        update(index) {
          if (index < 0) return;
          this.index = index;
          if (this.current) this.current.textContent = String(index + 1);
          if (this.prev) this.prev.disabled = index === 0;
          if (this.next) this.next.disabled = index >= this.pages.length - 1;
          this.pages.forEach((page, i) => page.setAttribute('aria-hidden', String(i !== index)));
        }
      }
    );
  }
})();
